
import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, User, Settings, LogOut, Heart, Calendar, MessageSquare, BookOpen, Pencil, Bell, Star, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useVendorServiceCache } from "@/hooks/useVendorServiceCache";

interface DashboardMenuItem {
  view: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface MobileMenuProps {
  isSignedIn: boolean;
  displayName?: string;
  isAdmin?: boolean;
  isServiceOwner?: boolean;
  onSignOut?: () => Promise<void>;
  serviceId?: string;
  dashboardItems?: DashboardMenuItem[];
  activeDashboardView?: string;
  onDashboardViewChange?: (view: string) => void;
  isOnDashboard?: boolean;
}

export const MobileMenu = ({
  isSignedIn,
  displayName = "User",
  isAdmin = false,
  isServiceOwner = false,
  onSignOut,
  serviceId,
  dashboardItems = [],
  activeDashboardView,
  onDashboardViewChange,
  isOnDashboard = false,
}: MobileMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSignOut = async () => {
    if (onSignOut) {
      setIsOpen(false);
      await onSignOut();
    }
  };

  const handleDashboardViewChange = (view: string) => {
    if (onDashboardViewChange) {
      setIsOpen(false);
      onDashboardViewChange(view);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-64">
        <SheetHeader className="mb-4">
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {isSignedIn ? (
            <>
              <div className="px-4 py-2">
                <p className="text-sm font-medium">Signed in as</p>
                <p className="font-bold">{displayName}</p>
              </div>
              <Separator />

              {isOnDashboard && dashboardItems.length > 0 && (
                <>
                  <div className="px-4 py-1">
                    <p className="text-sm font-medium text-gray-500">Service Management</p>
                  </div>
                  {dashboardItems.map((item) => (
                    <SheetClose key={item.view} asChild>
                      <Link
                        to={item.path}
                        className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md ${
                          activeDashboardView === item.view ? "bg-gray-100 font-medium" : ""
                        }`}
                      >
                        {item.icon}
                        <span className="ml-2">{item.label}</span>
                      </Link>
                    </SheetClose>
                  ))}
                  <Separator />
                </>
              )}
              
              {/* Show vendor navigation for service owners */}
              {isServiceOwner && !isOnDashboard && (
                <>
                  <div className="px-4 py-1">
                    <p className="text-sm font-medium text-gray-500">Vendor Navigation</p>
                  </div>
                  <SheetClose asChild>
                    <Link
                      to="/vendor/services"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <BookOpen className="mr-2 h-4 w-4" />
                      My Services
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/vendor/help"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <HelpCircle className="mr-2 h-4 w-4" />
                      Help
                    </Link>
                  </SheetClose>
                </>
              )}
              
              {/* Show regular user navigation for non-vendors */}
              {!isServiceOwner && !isOnDashboard && (
                <>
                  <SheetClose asChild>
                    <Link
                      to="/my-profile"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/favorites"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <Heart className="mr-2 h-4 w-4" />
                      My Favorites
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/my-bookings"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      My Bookings
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/my-reviews"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      My Reviews
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/my-notifications"
                      className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                    >
                      <Bell className="mr-2 h-4 w-4" />
                      Notifications
                    </Link>
                  </SheetClose>
                </>
              )}
              
              {isAdmin && (
                <SheetClose asChild>
                  <Link
                    to="/admin"
                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Admin Dashboard
                  </Link>
                </SheetClose>
              )}
              
              <Separator />
              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 rounded-md"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </button>
            </>
          ) : (
            <SheetClose asChild>
              <Link
                to="/auth"
                className="flex items-center justify-center px-4 py-2 bg-primary text-white rounded-md"
              >
                Sign In
              </Link>
            </SheetClose>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
